import React, { useRef, useState } from 'react';
import { Upload, Video, X } from 'lucide-react';

interface MediaUploadFieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (url: string) => void;
  onUpload: (file: File) => Promise<string>;
  /** Called after a successful upload – use to auto-save */
  onUploadComplete?: (url: string) => void;
  accept?: string;
}

export const MediaUploadField: React.FC<MediaUploadFieldProps> = ({
  label,
  hint,
  value,
  onChange,
  onUpload,
  onUploadComplete,
  accept = 'video/*',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const url = await onUpload(file);
      onChange(url);
      onUploadComplete?.(url);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setError(msg);
      console.error('Upload failed:', err);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="jm-field">
      <label className="jm-label">
        <Video size={14} />
        {label}
      </label>
      {hint && <span className="jm-field-hint jm-field-hint-top">{hint}</span>}
      <div className="jm-media-upload">
        {value ? (
          <div className="jm-media-preview jm-media-preview-video">
            <video src={value} muted playsInline className="jm-media-video-preview" />
            <div className="jm-media-preview-actions">
              <button
                type="button"
                className="jm-icon-btn"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                title="Replace"
              >
                {uploading ? <span className="jm-spin">⋯</span> : <Upload size={14} />}
              </button>
              <button
                type="button"
                className="jm-icon-btn jm-icon-btn-danger"
                onClick={() => onChange('')}
                title="Remove"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="jm-upload-zone"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <span className="jm-spin">Uploading…</span>
            ) : (
              <>
                <Upload size={20} />
                <span>Upload video</span>
              </>
            )}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          hidden
        />
      </div>
      <input
        className="jm-input jm-input-sm jm-input-muted"
        value={value}
        onChange={e => { onChange(e.target.value); setError(null); }}
        placeholder="Or paste URL"
        style={{ marginTop: 8 }}
      />
      {error && <span className="jm-field-error">{error}</span>}
    </div>
  );
};
